import type { IBaseResponse } from '~/interfaces/dto/base/request';

export enum ERegistrationStatus {
  INCOMPLETE = 0,
  PENDING = 1,
  COMPLETED = 2,
}

export interface IRegisterResponse extends IBaseResponse {
  data: {
    message: string;
    status: string;
    partialRegistrationStatus?: string;
    complianceResponse?: ICompliance;
  };
}

export interface IUserInfoExtended {
  id: string;
  betAccountId: string;
  email: string;
  username: string;
  firstname: string;
  lastname: string;
  languageOption: string;
  dialingCode: string;
  primaryMobileNumber: IMobileNumber;
  secondaryMobileNumbers?: IMobileNumber[];
  countryCode: string;
  currencyCode: string;
  idNumberType: string;
  idNumber: string;
  signupCode?: string;
  referralCode?: string;
  sourceOfFunds: string;
  dateOfBirth: string | Date;
  isRegistrationComplete: boolean;
  registrationStatus: ERegistrationStatus;
  brandId: string;
  accountBalances: {
    cashBalance: number;
    bonusBalance: number;
    ticketBalance: number;
    playThroughBalance: number;
    lastBonusCreditTransactionDateTime?: string;
  };
  originalQueryString?: string;
  twitterHandle?: string;
  defaultBetSize: number;
  allowInstantBet: boolean;
  autoAcceptLatestOdds: boolean;
  receivePromotionInformation: boolean;
  unsubscribeCorrespondence: boolean;
  proofOfIdDocumentStatus: number;
  proofOfResidenceDocumentStatus: number;
  proofOfBankDocumentStatus: number;
  sessionMetadata?: string;
  compliance?: ICompliance;
  complianceResponse?: ICompliance;
  securityQuestions?: ISecurityQuestions;
  registrationDateTime?: string;
}

export interface ISecurityQuestions {
  None?: string;
  PrimarySchool?: string;
  FirstPetsName?: string;
  LastFourDigits?: string;
  StreetName?: string;
  MothersMaidenName?: string;
}

export interface ICompliance {
  complianceStatus?: number;
  isValidId?: boolean;
  requiredComplianceStatus?: number;
  message?: string;
  accessToken?: string | null;
}

export interface IUserInfoExtendedResponse extends IBaseResponse {
  data: IUserInfoExtended;
}

export interface ILoginResponse {
  data?: {
    id?: string;
    jwtToken?: string;
    refreshToken?: string;
    username?: string;
    responsibleGaming?: null;
    complianceResponse?: {
      complianceStatus?: number;
      requiredComplianceStatus?: number;
      message?: string;
    };
  };
  isSuccessful: boolean;
  responseCode: number;
  error?: Record<string, string>;
  cause?: Record<string, string>;
  metadata?: Record<string, string>;
  httpCodeEnum: number;
}

export interface IMobileNumber {
  expiryDateTime: string;
  mobileNumber: string;
  primaryNumber: boolean;
  verified: boolean;
}

export interface IMobileNumbersResponse extends IBaseResponse {
  data: IMobileNumber[];
}

export interface IPartialRegStepOneResponse extends IBaseResponse {
  accountId: string;
  message: string;
  status: string;
  partialRegistrationStatus: string;
  complianceResponse?: {
    complianceStatus: string;
    requiredComplianceStatus: string;
    isValidId: boolean;
    message: string;
  };
}

export interface IVerifyPartialRegOTPResponse extends IBaseResponse {
  data: {
    id: string;
    sequentialId: number;
    email: string;
    username: string;
    firstname: string;
    lastname: string;
    dialingCode: string;
    primaryMobileNumber: {
      mobileNumber: string;
      primaryNumber: boolean;
      verified: boolean;
      expiryDateTime: string;
    };
    countryCode: string;
    idNumberType: string;
    idNumber: string;
    signupCode: string;
    referralCode: string;
    sourceOfFunds: string;
    dateOfBirth: string;
    brandId: string;
    currencyCode: string;
    languageOption: string;
    lockedStatus: boolean;
    isTestAccount: boolean;
    isBankingTestAccount: boolean;
  };
}

export interface IForgotLoginInitResponse extends IBaseResponse {
  data: {
    token: string;
    level: number;
    message: string;
  };
}

export interface IAddLimitResponse {
  data: IBrandLimits;
  isSuccessful: boolean;
  error: IError;
  metadata: string;
  httpStatusCode: string;
}
export interface IBrandLimits {
  brandId: string;
  restrictionTypeId: string;
  restrictionType: number;
  name: string;
  mappingCode: string;
  restrictionPermissions: IRestrictionPermissions;
}
export interface IWagerProgress extends IPlayerResponse {
  data: {
    accountId: string;
    wagerLimits: IWagerProgressType[];
  };
}
export interface IWagerProgressType {
  limitName: string;
  mappingCode: string;
  progressTowardThreshold: number;
  restrictionTypeId: string;
  threshold: number;
}
export interface IRestrictionPermissions {
  allowedPayout: boolean;
  allowedWithdrawal: boolean;
  allowedWager: boolean;
  allowedLogin: boolean;
}
interface IError {
  code: number;
  displayCode: number;
  message: string;
  remediation: string;
  additionalInformation: string;
}
export interface IBankingAccountSettingsResponse {
  data: IBankingAccountSettingsData | null;
  isSuccessful: boolean;
  error: unknown;
  metadata: unknown;
  httpStatusCode: number;
}

interface IBankingAccountSettingsData {
  allowWithdrawalReversal: boolean;
}
export interface IPlayerResponse {
  isSuccessful: boolean;
  error: IError;
  metadata: string;
  httpStatusCode: string;
}
export interface IBrandGetLimitsType {
  data: {
    restrictionTypeResponses: IUserLimitsRestrictionType[];
  };
  isSuccessful: boolean;
  error: IError;
  metadata: string;
  httpStatusCode: string;
}
export interface IUserGetLimitsType {
  data: {
    restrictionResponses?: IUserLimits[];
  };
  isSuccessful: boolean;
  error: IError;
  metadata: string;
  httpStatusCode: string;
}
export interface IUserLimitsRestrictionType {
  restrictionTypeId: string;
  brandId: string;
  restrictionType: number;
  name: string;
  isPermanent: boolean;
  isTimedRestriction: boolean;
  allowOverride: boolean;
  priority: number;
  mappingCode: string;
  restrictionId?: string;
  restrictionPermissions: IRestrictionPermissions;
  editedDateTime: Date;
  threshold?: number;
}
export interface IUserLimits {
  restrictionId: string;
  brandId: string;
  accountId?: string;
  threshold?: number;
  thresholdType?: number;
  createdDateTime: Date;
  editedDateTime: Date;
  isActive: boolean;
  restrictionType: IUserLimitsRestrictionType;
}
